// Browser persistence for saves: localStorage, spilling to IndexedDB for big saves.
// The sim package only (de)serializes; where saves live is the client's job (later: also the server).

import { saveKey } from "./net/mode";
import type { GameState } from "@degen/sim/state";
import { deserialize, serialize } from "@degen/sim/save";

const LS_LIMIT = 2 * 1024 * 1024;
const IDB_MARKER = "__idb__";

export interface KeyValueStore {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

function idb<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const open = indexedDB.open("degen-world", 1);
    open.onupgradeneeded = () => open.result.createObjectStore("saves");
    open.onerror = () => reject(open.error);
    open.onsuccess = () => {
      const tx = open.result.transaction("saves", mode);
      const req = fn(tx.objectStore("saves"));
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    };
  });
}

const defaultStore = (): KeyValueStore | null => (typeof localStorage !== "undefined" ? localStorage : null);

export async function saveGame(state: GameState, store = defaultStore()): Promise<void> {
  if (!store) return;
  const json = serialize(state);
  const key = saveKey();
  if (json.length > LS_LIMIT && typeof indexedDB !== "undefined") {
    await idb("readwrite", (s) => s.put(json, key));
    store.setItem(key, IDB_MARKER);
    return;
  }
  store.setItem(key, json);
}

export async function loadGame(store = defaultStore(), key = saveKey()): Promise<GameState | null> {
  if (!store) return null;
  let json = store.getItem(key);
  if (json === IDB_MARKER && typeof indexedDB !== "undefined") json = (await idb<string>("readonly", (s) => s.get(key))) ?? null;
  if (!json) return null;
  return deserialize(json);
}

export function hasSave(store = defaultStore()): boolean {
  return !!store?.getItem(saveKey());
}

export async function deleteSave(store = defaultStore()): Promise<void> {
  const key = saveKey();
  if (store?.getItem(key) === IDB_MARKER && typeof indexedDB !== "undefined") await idb("readwrite", (s) => s.delete(key));
  store?.removeItem(key);
}
